export type BrandThemeKey = "orange" | "aamalBlue";

export interface BrandTheme {
  primary: string;
  primaryDark: string;
  readingPrimary: string;
  lightCard: string;
  lightCardHeader: string;
  lightTextMuted: string;
  lightBorder: string;
  lightBackgroundAccent: string;
  darkBackground: string;
  darkSurface: string;
  darkCard: string;
  darkCardHeader: string;
  darkNavBar: string;
  darkTextMuted: string;
  darkTextSubtle: string;
  darkBorder: string;
  darkBorderStrong: string;
  readingBackground: string;
  readingSurface: string;
  readingCard: string;
  readingCardHeader: string;
  readingText: string;
  readingTextMuted: string;
  readingTextSubtle: string;
  readingBorder: string;
}

export const brandThemes = {
  orange: {
    primary: "#FF7900",
    primaryDark: "#E66B00",
    readingPrimary: "#B35900",
    lightCard: "#F7F7F7",
    lightCardHeader: "#F1F1F1",
    lightTextMuted: "#716453",
    lightBorder: "#E3D9CB",
    lightBackgroundAccent: "#F6F2EE",
    darkBackground: "#232323",
    darkSurface: "#3B3B3B",
    darkCard: "#313131",
    darkCardHeader: "#3B3B3B",
    darkNavBar: "#313131",
    darkTextMuted: "#9CA3AF",
    darkTextSubtle: "#6B7280",
    darkBorder: "rgba(255,255,255,0.1)",
    darkBorderStrong: "rgba(255,255,255,0.16)",
    readingBackground: "#FAF7F0",
    readingSurface: "#FAF7F0",
    readingCard: "#F3EDE3",
    readingCardHeader: "#EEE7DC",
    readingText: "#3E2723",
    readingTextMuted: "#6D5C5A",
    readingTextSubtle: "#A69076",
    readingBorder: "#E3D9CB",
  },
  aamalBlue: {
    primary: "#1686C8",
    primaryDark: "#126B9E",
    readingPrimary: "#0F6EAA",
    lightCard: "#F2F8FC",
    lightCardHeader: "#E9F2F8",
    lightTextMuted: "#587282",
    lightBorder: "#D5E6F0",
    lightBackgroundAccent: "#EAF5FB",
    darkBackground: "#0C141A",
    darkSurface: "#13212A",
    darkCard: "#101B22",
    darkCardHeader: "#16252F",
    darkNavBar: "#101B22",
    darkTextMuted: "#9FB5C1",
    darkTextSubtle: "#6C8796",
    darkBorder: "#214154",
    darkBorderStrong: "#2B536A",
    readingBackground: "#F4FAFD",
    readingSurface: "#F4FAFD",
    readingCard: "#EAF4FA",
    readingCardHeader: "#E2EFF8",
    readingText: "#17374A",
    readingTextMuted: "#5A7485",
    readingTextSubtle: "#8EA5B3",
    readingBorder: "#D5E6F0",
  },
} as const satisfies Record<BrandThemeKey, BrandTheme>;

export const ACTIVE_BRAND_THEME: BrandThemeKey = "aamalBlue";

export const activeBrand = brandThemes[ACTIVE_BRAND_THEME];

export const withAlpha = (hex: string, alphaHex: string): string =>
  /^#[0-9A-F]{6}$/i.test(hex) ? `${hex}${alphaHex}` : hex;

const toRgbChannels = (hex: string): string => {
  const normalized = hex.replace("#", "");

  if (normalized.length !== 6) {
    return "0, 0, 0";
  }

  const parts = normalized.match(/.{2}/g);
  if (!parts) {
    return "0, 0, 0";
  }

  return parts.map((part) => parseInt(part, 16)).join(", ");
};

export const getBrandThemeCssVariables = (
  theme: BrandTheme = activeBrand,
): Record<string, string> => ({
  "--theme-primary": theme.primary,
  "--theme-primary-dark": theme.primaryDark,
  "--theme-reading-primary": theme.readingPrimary,
  "--theme-light-card": theme.lightCard,
  "--theme-light-card-header": theme.lightCardHeader,
  "--theme-light-text-muted": theme.lightTextMuted,
  "--theme-light-border": theme.lightBorder,
  "--theme-light-background-accent": theme.lightBackgroundAccent,
  "--theme-dark-background": theme.darkBackground,
  "--theme-dark-surface": theme.darkSurface,
  "--theme-dark-card": theme.darkCard,
  "--theme-dark-card-header": theme.darkCardHeader,
  "--theme-dark-navbar": theme.darkNavBar,
  "--theme-dark-text-muted": theme.darkTextMuted,
  "--theme-dark-text-subtle": theme.darkTextSubtle,
  "--theme-dark-border": theme.darkBorder,
  "--theme-dark-border-strong": theme.darkBorderStrong,
  "--theme-reading-background": theme.readingBackground,
  "--theme-reading-surface": theme.readingSurface,
  "--theme-reading-card": theme.readingCard,
  "--theme-reading-card-header": theme.readingCardHeader,
  "--theme-reading-text": theme.readingText,
  "--theme-reading-text-muted": theme.readingTextMuted,
  "--theme-reading-text-subtle": theme.readingTextSubtle,
  "--theme-reading-border": theme.readingBorder,
  "--theme-primary-rgb": toRgbChannels(theme.primary),
  "--theme-primary-dark-rgb": toRgbChannels(theme.primaryDark),
  "--theme-reading-primary-rgb": toRgbChannels(theme.readingPrimary),
  "--theme-dark-background-rgb": toRgbChannels(theme.darkBackground),
  "--theme-dark-surface-rgb": toRgbChannels(theme.darkSurface),
  "--theme-dark-card-rgb": toRgbChannels(theme.darkCard),
  "--theme-dark-card-header-rgb": toRgbChannels(theme.darkCardHeader),
  "--theme-reading-card-rgb": toRgbChannels(theme.readingCard),
  "--theme-reading-background-rgb": toRgbChannels(theme.readingBackground),
  "--theme-primary-05": withAlpha(theme.primary, "0D"),
  "--theme-primary-08": withAlpha(theme.primary, "14"),
  "--theme-primary-12": withAlpha(theme.primary, "1F"),
  "--theme-primary-18": withAlpha(theme.primary, "2E"),
  "--theme-primary-30": withAlpha(theme.primary, "4D"),
  "--theme-primary-40": withAlpha(theme.primary, "66"),
  "--theme-primary-55": withAlpha(theme.primary, "8C"),
  "--theme-primary-70": withAlpha(theme.primary, "B3"),
  "--theme-primary-90": withAlpha(theme.primary, "E6"),
  "--theme-primary-dark-08": withAlpha(theme.primaryDark, "14"),
  "--theme-primary-dark-12": withAlpha(theme.primaryDark, "1F"),
  "--theme-primary-dark-18": withAlpha(theme.primaryDark, "2E"),
  "--theme-primary-dark-80": withAlpha(theme.primaryDark, "CC"),
  "--bg-0": theme.darkBackground,
  "--bg-1": theme.darkCard,
  "--bg-2": theme.darkSurface,
  "--bg-3": theme.darkCardHeader,
  "--accent": theme.primary,
  "--accent-dark": theme.primaryDark,
});

export const applyBrandTheme = (
  target: HTMLElement,
  theme: BrandTheme = activeBrand,
): void => {
  const cssVariables = getBrandThemeCssVariables(theme);

  Object.entries(cssVariables).forEach(([name, value]) => {
    target.style.setProperty(name, value);
  });

  target.setAttribute("data-brand-theme", ACTIVE_BRAND_THEME);
};
